// Main menu DOM overlay. Sits above the canvas; player picks a game mode and
// a seed, presses Start, the overlay hides and App.startMatch is invoked.
//
// DOM so the inputs (seed number field, mode <select>, checkbox)
// are native browser controls — no need to reinvent text input. Pattern
// mirrors ReplayOverlay.ts.

import { ROLE_PROGRESSION, type RosterSizes } from "@arcade-hockey/sim";

export type MatchMode = "pvcpu" | "cpuvcpu";

export type MainMenuConfig = {
  seed: number;
  mode: MatchMode;
  autoCaptureReplay: boolean;
  rosterSizes: RosterSizes;
};

const DEFAULT_SKATER_COUNT = ROLE_PROGRESSION.length;

export class MainMenu {
  private root: HTMLDivElement;
  private seedInput: HTMLInputElement;
  private modeSelect: HTMLSelectElement;
  private autoCaptureCheckbox: HTMLInputElement;
  private homeSkatersSelect: HTMLSelectElement;
  private awaySkatersSelect: HTMLSelectElement;
  private startButton: HTMLButtonElement;
  private status: HTMLDivElement;
  private onStart: (config: MainMenuConfig) => void;

  constructor(onStart: (config: MainMenuConfig) => void) {
    this.onStart = onStart;

    this.root = document.createElement("div");
    Object.assign(this.root.style, OVERLAY_STYLE);

    const heading = document.createElement("div");
    heading.textContent = "Arcade Hockey";
    Object.assign(heading.style, HEADING_STYLE);

    const subheading = document.createElement("div");
    subheading.textContent = "Pick a mode and start a match.";
    Object.assign(subheading.style, SUBHEADING_STYLE);

    // Mode select.
    this.modeSelect = document.createElement("select");
    Object.assign(this.modeSelect.style, INPUT_STYLE);
    addOption(this.modeSelect, "pvcpu", "Player vs CPU");
    addOption(this.modeSelect, "cpuvcpu", "CPU vs CPU");
    this.modeSelect.value = "pvcpu";
    this.modeSelect.addEventListener("change", () => this.onModeChanged());

    // Seed input — defaults to a random 31-bit integer each time the menu
    // mounts. User can override by typing.
    this.seedInput = document.createElement("input");
    this.seedInput.type = "number";
    this.seedInput.min = "0";
    this.seedInput.max = String(0x7fffffff);
    this.seedInput.value = String(randomSeed());
    Object.assign(this.seedInput.style, { ...INPUT_STYLE, width: "140px" });

    const reseedButton = makeButton("⟲", () => {
      this.seedInput.value = String(randomSeed());
    });
    reseedButton.title = "Pick a new random seed";
    Object.assign(reseedButton.style, { ...BUTTON_STYLE, padding: "4px 8px" });

    const seedRow = document.createElement("div");
    Object.assign(seedRow.style, ROW_STYLE);
    seedRow.append(this.seedInput, reseedButton);

    // Auto-capture-replay checkbox. Default ON for CPU-vs-CPU (so every
    // automated match writes a file we can inspect later), OFF for
    // Player-vs-CPU (don't pester the user with downloads they didn't ask
    // for).
    this.autoCaptureCheckbox = document.createElement("input");
    this.autoCaptureCheckbox.type = "checkbox";
    this.autoCaptureCheckbox.checked = false; // adjusted in onModeChanged below

    const captureLabel = document.createElement("label");
    Object.assign(captureLabel.style, CHECKBOX_LABEL_STYLE);
    captureLabel.append(this.autoCaptureCheckbox);
    const captureText = document.createElement("span");
    captureText.textContent = "Auto-save replay JSON when match ends";
    captureLabel.append(captureText);

    // Per-team skater count 0–5 (goalies always present). Default to the
    // canonical 5v5 so the menu's "Start" matches today's behaviour
    // verbatim. Roles fill in the order defined by ROLE_PROGRESSION;
    // the option label spells the resulting line out so the user
    // doesn't have to memorise the table.
    this.homeSkatersSelect = makeSkaterCountSelect();
    this.awaySkatersSelect = makeSkaterCountSelect();

    this.startButton = makeButton("Start match", () => this.onStartClicked());
    Object.assign(this.startButton.style, START_BUTTON_STYLE);

    this.status = document.createElement("div");
    Object.assign(this.status.style, STATUS_STYLE);
    this.status.textContent =
      "Tip: in dev builds, press F9 once a match is running to open the replay panel.";

    this.root.append(
      heading,
      subheading,
      labeled("Mode", this.modeSelect),
      labeled("Seed", seedRow),
      labeled("Home skaters", this.homeSkatersSelect),
      labeled("Away skaters", this.awaySkatersSelect),
      captureLabel,
      this.startButton,
      this.status,
    );

    // Initial mode-specific defaults.
    this.onModeChanged();
  }

  attach(parent: HTMLElement): void {
    parent.append(this.root);
  }

  detach(): void {
    this.root.remove();
  }

  show(): void {
    this.root.style.display = "flex";
  }

  hide(): void {
    this.root.style.display = "none";
  }

  private onModeChanged(): void {
    const mode = this.modeSelect.value as MatchMode;
    if (mode === "cpuvcpu") {
      this.autoCaptureCheckbox.checked = true;
    } else {
      this.autoCaptureCheckbox.checked = false;
    }
  }

  private onStartClicked(): void {
    const seedValue = Number.parseInt(this.seedInput.value, 10);
    if (!Number.isFinite(seedValue) || seedValue < 0) {
      this.status.textContent = "Seed must be a non-negative integer.";
      return;
    }
    const mode = this.modeSelect.value as MatchMode;
    this.onStart({
      seed: seedValue,
      mode,
      autoCaptureReplay: this.autoCaptureCheckbox.checked,
      rosterSizes: {
        home: Number.parseInt(this.homeSkatersSelect.value, 10),
        away: Number.parseInt(this.awaySkatersSelect.value, 10),
      },
    });
  }
}

function makeSkaterCountSelect(): HTMLSelectElement {
  const select = document.createElement("select");
  Object.assign(select.style, INPUT_STYLE);
  for (let n = 0; n <= ROLE_PROGRESSION.length; n++) {
    const opt = document.createElement("option");
    opt.value = String(n);
    opt.textContent =
      n === 0 ? "0 (goalie only)" : `${n} (${ROLE_PROGRESSION.slice(0, n).join(", ")})`;
    select.append(opt);
  }
  select.value = String(DEFAULT_SKATER_COUNT);
  return select;
}

function randomSeed(): number {
  return Math.floor(Math.random() * 0x7fffffff);
}

function addOption(select: HTMLSelectElement, value: string, label: string): void {
  const opt = document.createElement("option");
  opt.value = value;
  opt.textContent = label;
  select.append(opt);
}

function makeButton(label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.textContent = label;
  b.addEventListener("click", onClick);
  Object.assign(b.style, BUTTON_STYLE);
  return b;
}

function labeled(label: string, control: HTMLElement): HTMLDivElement {
  const row = document.createElement("div");
  Object.assign(row.style, LABELED_ROW_STYLE);
  const tag = document.createElement("span");
  tag.textContent = label;
  Object.assign(tag.style, LABEL_TEXT_STYLE);
  row.append(tag, control);
  return row;
}

const OVERLAY_STYLE: Partial<CSSStyleDeclaration> = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: "9000",
  minWidth: "320px",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  padding: "24px",
  background: "rgba(14, 17, 22, 0.96)",
  color: "#e6edf3",
  font: "13px ui-monospace, SFMono-Regular, Menlo, monospace",
  border: "1px solid #2d3748",
  borderRadius: "8px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
};

const HEADING_STYLE: Partial<CSSStyleDeclaration> = {
  fontSize: "18px",
  fontWeight: "700",
  letterSpacing: "0.04em",
};

const SUBHEADING_STYLE: Partial<CSSStyleDeclaration> = {
  color: "#9ca3af",
  fontSize: "12px",
  marginBottom: "4px",
};

const LABELED_ROW_STYLE: Partial<CSSStyleDeclaration> = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};

const LABEL_TEXT_STYLE: Partial<CSSStyleDeclaration> = {
  color: "#9ca3af",
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const INPUT_STYLE: Partial<CSSStyleDeclaration> = {
  background: "#0e1116",
  color: "#e6edf3",
  border: "1px solid #4a5568",
  borderRadius: "4px",
  padding: "6px 8px",
  fontFamily: "inherit",
  fontSize: "13px",
};

const ROW_STYLE: Partial<CSSStyleDeclaration> = {
  display: "flex",
  gap: "6px",
  alignItems: "center",
};

const CHECKBOX_LABEL_STYLE: Partial<CSSStyleDeclaration> = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "12px",
  color: "#cbd5e0",
  cursor: "pointer",
};

const BUTTON_STYLE: Partial<CSSStyleDeclaration> = {
  background: "#2d3748",
  color: "#e6edf3",
  border: "1px solid #4a5568",
  borderRadius: "4px",
  padding: "6px 12px",
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: "13px",
};

const START_BUTTON_STYLE: Partial<CSSStyleDeclaration> = {
  marginTop: "8px",
  background: "#3a6db5",
  color: "#ffffff",
  border: "1px solid #4a87d6",
  borderRadius: "4px",
  padding: "10px 16px",
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: "14px",
  fontWeight: "600",
  letterSpacing: "0.02em",
};

const STATUS_STYLE: Partial<CSSStyleDeclaration> = {
  color: "#6b7280",
  fontSize: "11px",
  minHeight: "14px",
};
